import io from "socket.io-client";
import Keys from "Keys";

let socket;
let url = Keys.baseUrl;
let controlling;

// ── Per-surface notifications-topic registry (Kafka Phase 3, 2026-08-05) ──────────────
// The notifications socket is a SHARED singleton that survives SPA route changes, and the
// server is REPLACE-semantics (a `subscribe` releases any topic NOT in the newest list —
// notificationsMS/listeners/realtime.js). The old `topicsGlobal = topics` overwrite let
// whichever surface called getSocket LAST clobber every other mounted surface's
// subscription; a naive monotonic union would instead grow unbounded and cross the server
// cap MAX_TOPICS_PER_SOCKET=50, darkening the just-opened surface while pinning idle
// consumers for off-screen devices. This registry keys each mounted surface's topic set
// separately (REPLACE-on-call, so an in-place asset switch UPDATES a surface's entry rather
// than accumulating), drops it on unmount via leaveSurface(), and always emits the
// RECOMPUTED, deduped UNION of currently-registered surfaces — bounded to a handful.
const surfaceTopics = new Map(); // surfaceKey -> string[]

function unionTopics() {
  const set = new Set();
  surfaceTopics.forEach((topics) => topics.forEach((t) => set.add(t)));
  return [...set];
}

function emitSubscribe() {
  if (socket) socket.emit("subscribe", { topics: unionTopics() });
}

function controllingSocket() {
  let path = url.includes("/api") ? "/api/controlling/socket.io" : "/socket.io/controlling"

  controlling = io(url, {
    path: path,
    transports: ["websocket"],
    upgrade: false,
    query: {
      token: window.localStorage.getItem("token"),
    },
  });

  controlling.on("connect", () => {
    // console.log("Socket IO Connected", "controlling");
  });

  controlling.on("disconnect", () => {
    // console.log("Socket IO Disonnected", "controlling");
    controlling = null;
  });

  return controlling;
}

// Shared notifications socket. Each caller registers its topic set under a STABLE
// `surfaceKey`; the emitted subscription is always the union of all registered surfaces.
// Call leaveSurface(surfaceKey) on unmount to drop a surface's topics (the server reconcile
// then releases any topic no longer needed by a mounted surface).
function getSocket(topics, surfaceKey = "default") {
  let path = url.includes("/api") ? "/api/notification/socket.io" : "/socket.io/notifications"

  // REPLACE (never append) this surface's registered set.
  surfaceTopics.set(surfaceKey, Array.isArray(topics) ? topics : []);

  if (!socket) {
    socket = io(url, {
      path: path,
      transports: ["websocket"],
      upgrade: false,
      query: {
        token: window.localStorage.getItem("token"),
      },
    });

    socket.on("connect", () => {
      // console.log("Socket IO Connected");
      emitSubscribe();
    });

    socket.on("disconnect", () => {
      // Do NOT null `socket` here. socket.io-client auto-reconnects on THIS same instance and
      // re-fires the "connect" handler above, which re-emits the surfaceTopics union — but only
      // while `socket` is truthy (emitSubscribe's guard). Nulling it defeated that re-subscribe
      // (every surface stayed dark after a pod-restart/network blip until a remount) AND made the
      // next getSocket() build a second, orphaned io() instance. The singleton is meant to live
      // for the app session (see header); socket.io manages the underlying connection itself.
    });
  }

  emitSubscribe();

  return socket;
}

function leaveSurface(surfaceKey) {
  if (surfaceTopics.delete(surfaceKey)) emitSubscribe();
}

// Full teardown for an EXPLICIT sign-out. The disconnect handler deliberately does NOT null the
// singleton (so transient reconnects re-subscribe), which means a same-tab logout -> login would
// otherwise reuse the FIRST user's token-authenticated connection. logoutFn calls this to force a
// clean rebuild with the next user's token on the next getSocket()/controllingSocket().
function resetSocket() {
  if (socket) socket.disconnect();
  socket = null;
  if (controlling) controlling.disconnect();
  controlling = null;
  surfaceTopics.clear();
}

export {
  controllingSocket,
  getSocket,
  leaveSurface,
  resetSocket,
};
